import { AxiosResponse } from "axios";
import axios, { APIWrapperResponse, DefaultResponse } from "../general/HttpClient";
import { LeagueViewModel } from "../leagues/LeaguesStore";
import { SeasonViewModel } from "../seasons/SeasonsStore";
import { StatViewModel } from "../stats/StatsStore";
import { TeamViewModel } from "../teams/TeamsStore";
import { TournamentMatchViewModel, TournamentViewModel } from "../tournaments/TournamentsStore";
import { AliasViewModel } from "../aliases/AliasStore";

export function getMatchDays() {
    return axios.get(`/api/Matches/getMatchDays/`).then((response: AxiosResponse<APIWrapperResponse<Array<string>>>) => {
        return response.data.result;
    });
}

export function getMatchesBetween(startDate: any, endDate: any) {
    return axios.get(`/api/Matches/getMatchesBetween`, { params: { startDate, endDate } }).then((response: AxiosResponse<APIWrapperResponse<Array<MatchViewModel>>>) => {
        return response.data.result;
    });
}

export function getMatches(upcoming: any, past: any, archived: any, skip = 0, limit = 50) {
    return axios.get("/api/Matches/getMatches", { params: { archived, upcoming, past, skip, limit } }).then((response: AxiosResponse<APIWrapperResponse<Array<MatchViewModel>>>) => {
        return response.data.result;
    });
}

export function getAdHocMatches(upcoming: any, past: any, archived: any, skip = 0, limit = 50) {
    return axios.get("/api/Matches/getAdHocMatches", { params: { upcoming, past, archived, skip, limit } }).then((response: AxiosResponse<APIWrapperResponse<Array<MatchViewModel>>>) => {
        return response.data.result;
    });
}

export function getTeamMatches(id) {
    return axios.get(`/api/Matches/getTeamMatches/${id}`).then((response: AxiosResponse<APIWrapperResponse<Array<MatchViewModel>>>) => {
        return response.data.result;
    });
}

export function getAliasMatches(id) {
    return axios.get(`/api/Matches/getAliasMatches/${id}`).then((response: AxiosResponse<APIWrapperResponse<Array<MatchViewModel>>>) => {
        return response.data.result;
    });
}

export function getMatch(id) {
    return axios.get(`/api/Matches/getMatch/${id}`).then((response: AxiosResponse<APIWrapperResponse<MatchViewModel>>) => {
        return response.data.result;
    });
}

export function createMatch(request: CreateMatchRequest) {
    return axios.post(`/api/Matches/createMatch`, request).then((response: AxiosResponse<APIWrapperResponse<CreateMatchResponse>>) => {
        return response.data.result;
    });
}

export function updateMatch(request: UpdateMatchRequest) {
    return axios.put(`/api/Matches/updateMatch`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function addParticipant(request: AddMatchParticipantRequest) {
    return axios.post(`/api/Matches/addParticipant`, request).then((response: AxiosResponse<APIWrapperResponse<AddMatchParticipantResponse>>) => {
        return response.data.result;
    });
}

export function removeParticipant(request: RemoveMatchParticipantRequest) {
    return axios.post(`/api/Matches/removeParticipant`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function addOutcome(request: AddMatchOutcomeRequest) {
    return axios.post(`/api/Matches/addOutcome`, request).then((response: AxiosResponse<APIWrapperResponse<AddMatchOutcomeResponse>>) => {
        return response.data.result;
    });
}

export function removeOutcome(request: RemoveMatchOutcomeRequest) {
    return axios.post(`/api/Matches/removeOutcome`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function addStat(request: AddStatRequest) {
    return axios.post(`/api/Matches/addStat`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export function removeStat(request: RemoveStatRequest) {
    return axios.post(`/api/Matches/removeStat`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}
export function updateMatchStat(request: UpdateStatRequest) {
    return axios.post(`/api/Matches/updateStat`, request).then((response: AxiosResponse<APIWrapperResponse<DefaultResponse>>) => {
        return response.data.result;
    });
}

export class MatchViewModel {
    constructor() {
        this.participants = [];
        this.outcomes = [];
        this.stats = [];
    }
    id: string;
    startDate: string;
    isUpcoming: boolean;
    isPublic: boolean;
    logoUri?: string;
    splashUri?: string;
    color?: string;
    archived?: boolean;
    tournamentID: string;
    tournament: TournamentViewModel;
    tournamentMatchID: number;
    seasonID: string;
    season: SeasonViewModel;
    leagueID: string;
    league: LeagueViewModel;
    participants: MatchParticipantViewModel[];
    outcomes: MatchOutcomeViewModel[];
    stats: MatchStatOverview[];
}

export interface MatchParticipantViewModel {
    id: string;
    teamID: string;
    team: TeamViewModel;
    sortKey: number;
    outcomes: MatchOutcomeViewModel[];
    stats: MatchStatViewModel[];
    aliases: AliasMatchParticipantViewModel[];
}

export interface MatchOutcomeViewModel {
    id?: string;
    score: number;
    description: string;
    timestamp?: string;
    outcomeType: string;
    participantID: string;
    participant: MatchParticipantViewModel;
}

export interface MatchStatViewModel {
    id?: string;
    value: number;
    participantID: string;
    statID: string;
    stat: StatViewModel;
}

export class CreateMatchRequest {
    constructor() {
        this.participantIDs = [];
    }
    participantIDs: string[];
    startDate: string;
    isPublic: boolean;
    logoUri?: string;
    splashUri?: string;
    color?: string;
}

export interface CreateMatchResponse {
    success: boolean;
    matchID: string;
    errors: string[];
}

export interface UpdateMatchRequest {
    id: string;
    startDate: string;
    isPublic: boolean;
    logoUri?: string;
    splashUri?: string;
    color?: string;
}

export interface AddMatchOutcomeRequest {
    matchID: string;
    participantID: string;
    description: string;
    outcomeType: string;
    score: number;
}

export interface AddMatchOutcomeResponse extends DefaultResponse {
    outcomeID: string;
}

export interface AddMatchParticipantRequest {
    matchID: string;
    teamID: string;
}

export interface AddMatchParticipantResponse extends DefaultResponse {
    teamID: string;
}

export interface RemoveMatchOutcomeRequest {
    matchID: string;
    outcomeID: string;
}

export interface RemoveMatchParticipantRequest {
    matchID: string;
    teamID: string;
}

export interface OutcomeType {
    name: string;
    value: number;
    displayName: string;
}

export interface AddStatRequest {
    matchID: string;
    participantID: string;
    statID: string;
    value: number;
}

export interface RemoveStatRequest {
    matchID: string;
    matchStatID: string;
}

export interface UpdateStatRequest {
    matchID: string;
    matchStatID: string;
    value: number;
}

export interface MatchStatOverview {
    statID: string;
    stat: StatViewModel;
    teams: MatchStatOverviewTeam[];
}

export interface MatchStatOverviewTeam {
    teamID: string;
    team: TeamViewModel;
    value: number;
}


export interface AliasMatchParticipantViewModel {
    id: string;
    participantID: string;
    aliasID: string;
    alias: AliasViewModel;
    stats: AliasMatchStatViewModel[];
}

export interface AliasMatchStatViewModel {
    id: string;
    value: number;
    participantID: string;
    statID: string;
    stat: StatViewModel;
}